import { type UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { ItemsApi } from '@/common/api/items';
import {
  invalidateItemsTreeSet,
  treeSetLacksItems,
} from '@/common/queries/itemsTreeSetConsistency';
import {
  excludeFromRemovedNotice,
  forgetFromRemovedNotice,
  retractRemovedNoticeToast,
} from '@/common/queries/itemsTreeSetRemovedNotice';
import { reconcileAndSettle } from '@/common/queries/itemsTreeSetSettleState';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { IntegrationItemsApi } from '@/features/settings/work-integrations/api/workIntegrationItems';

type DeleteItemVariables = { itemId: number; isOrphanedIntegrationItem?: boolean };

type DeleteItemMutationOptions = Omit<
  UseMutationOptions<void, Error, DeleteItemVariables>,
  'mutationFn'
>;

export const useDeleteItemMutation = (options?: DeleteItemMutationOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ itemId, isOrphanedIntegrationItem }: DeleteItemVariables) =>
      isOrphanedIntegrationItem
        ? IntegrationItemsApi.deleteOrphanedIntegrationItem({ itemId })
        : ItemsApi.deleteItem({ itemId }),
    // Registered before the request is sent, so a concurrent reconciliation can't publish a
    // removed notice first — undone in onSettled if the delete turns out to have failed.
    onMutate: (variables, context) => {
      excludeFromRemovedNotice(queryClient, [variables.itemId]);
      return options?.onMutate?.(variables, context);
    },
    onSettled: async (...args) => {
      const [, error, variables] = args;
      if (error) {
        // The item is still there — don't leave it suppressed by the optimistic exclusion.
        forgetFromRemovedNotice(queryClient, [variables.itemId]);
      } else {
        // Retracts any already-shown toast's Open action for this item — it's gone now.
        retractRemovedNoticeToast(queryClient, variables.itemId);
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: commonQueryKeys.items.all() }),
        queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemsDataSet.all() }),
        invalidateItemsTreeSet(queryClient),
        queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemsBulkFetch.all() }),
        queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemsBulkFetchV2.all() }),
        queryClient.invalidateQueries({ queryKey: commonQueryKeys.item(variables.itemId) }),
        queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemDescendants.all() }),
        queryClient.invalidateQueries({ queryKey: ['itemAncestors'] }),
      ]);
      // A failed delete leaves the row in place, so there is nothing to poll for.
      if (!error) {
        // Refreshes the TTL so a slow request/invalidation round trip can't expire it too early.
        excludeFromRemovedNotice(queryClient, [variables.itemId]);
        void reconcileAndSettle(queryClient, [variables.itemId], {
          isReflected: treeSetLacksItems([variables.itemId]),
        });
      }
      await options?.onSettled?.(...args);
    },
  });
};
